import React, { useRef, useState, useEffect } from "react";
import Scanner from "../../../api/Scanner";

const LicenseScanner = ({ onClose, onScanComplete }) => {
	const videoRef = useRef(null);
	const canvasRef = useRef(null);
	const [stream, setStream] = useState(null);
	const [results, setResults] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [isCameraActive, setIsCameraActive] = useState(false);
	const [scanSuccessful, setScanSuccessful] = useState(false);
	const [showScanner, setShowScanner] = useState(true);

	// Initialize camera on component mount
	useEffect(() => {
		startCamera();
		return () => {
			if (stream) {
				stream.getTracks().forEach((track) => track.stop());
			}
		};
	}, []);

	const startCamera = async () => {
		try {
			const constraints = {
				video: {
					width: { ideal: 1280 },
					height: { ideal: 720 },
					facingMode: "environment",
				},
			};
			const mediaStream = await navigator.mediaDevices.getUserMedia(
				constraints
			);
			setStream(mediaStream);
			setIsCameraActive(true);
			if (videoRef.current) {
				videoRef.current.srcObject = mediaStream;
				videoRef.current
					.play()
					.catch((e) => console.error("Error playing video:", e));
			}
		} catch (err) {
			setError("Camera access failed: " + err.message);
		}
	};

	const stopCamera = () => {
		if (stream) {
			stream.getTracks().forEach((track) => track.stop());
			setStream(null);
			setIsCameraActive(false);
			if (videoRef.current) videoRef.current.srcObject = null;
		}
		setShowScanner(false);
		if (typeof onClose === "function") onClose();
	};

	function parseLicenseData(rawText) {
		const parsed = {};
		const lines = rawText.split("\n");

		lines.forEach((line) => {
			const exactMatch = line.match(/^([A-Z]{3})(.*)$/);
			if (exactMatch) {
				parsed[exactMatch[1]] = exactMatch[2].trim();
			} else {
				const embeddedMatch = line.match(/(DAQ|DBB|DBA)([^\n\r]*)/g);
				if (embeddedMatch) {
					embeddedMatch.forEach((entry) => {
						const key = entry.slice(0, 3);
						parsed[key] = entry.slice(3).trim();
					});
				}
			}
		});

		const formatDate = (raw) => {
			if (!raw || raw.length !== 8) return "";
			const mm = raw.slice(0, 2);
			const dd = raw.slice(2, 4);
			const yyyy = raw.slice(4);
			return `${yyyy}-${mm}-${dd}`;
		};

		return {
			firstName: parsed["DAC"] || "",
			middleName: parsed["DAD"] || "",
			lastName: parsed["DCS"] || "",
			licenseNumber: parsed["DAQ"] || "",
			address: parsed["DAG"] || "",
			city: parsed["DAI"] || "",
			state: parsed["DAJ"] || "",
			zip: parsed["DAK"] ? parsed["DAK"].slice(0, 5) : "",
			dob: formatDate(parsed["DBB"]),
			expiration: formatDate(parsed["DBA"]),
			gender:
				parsed["DBC"] === "1" ? "Male" : parsed["DBC"] === "2" ? "Female" : "",
		};
	}

	const captureImage = () => {
		if (!videoRef.current || !canvasRef.current) return;
		const canvas = canvasRef.current;
		const video = videoRef.current;
		if (video.paused || video.ended) return;
		canvas.width = video.videoWidth || 640;
		canvas.height = video.videoHeight || 480;
		const ctx = canvas.getContext("2d");
		try {
			ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
			canvas.toBlob(
				async (blob) => {
					if (blob)
						await processImage(
							new File([blob], "license-capture.jpg", { type: "image/jpeg" })
						);
					else setError("Failed to capture image");
				},
				"image/jpeg",
				0.95
			);
		} catch (err) {
			setError("Failed to capture image: " + err.message);
		}
	};

	const handleFileSelect = (event) => {
		const file = event.target.files[0];
		if (file) processImage(file);
	};

	const processImage = async (imageFile) => {
		setIsLoading(true);
		setError(null);
		try {
			const scanResults = await Scanner(imageFile);
			const rawText = scanResults?.results?.[0]?.text;
			if (!rawText || rawText.toLowerCase().includes("no barcode detected")) {
				setError("No barcode detected. Please try again with a clearer image.");
				setResults(null);
				return;
			}
			const parsed = parseLicenseData(rawText);
			setResults(parsed);
			setScanSuccessful(true);
			if (typeof onScanComplete === "function") onScanComplete(parsed);
		} catch (err) {
			setError("Scan failed: " + err.message);
		} finally {
			setIsLoading(false);
		}
	};

	const resetScanner = () => {
		setResults(null);
		setError(null);
		setScanSuccessful(false);
	};

	return (
		<div
			className="scanner-container"
			style={{ marginBottom: "20px", display: showScanner ? "block" : "none" }}
		>
			{!scanSuccessful ? (
				<>
					<div className="scanner-wrapper" style={{ marginBottom: "10px" }}>
						<div
							className="camera-container"
							style={{
								width: "100%",
								height: "200px",
								backgroundColor: "#000",
								position: "relative",
								overflow: "hidden",
								borderRadius: "4px",
							}}
						>
							<video
								ref={videoRef}
								autoPlay
								playsInline
								muted
								style={{
									width: "100%",
									height: "100%",
									objectFit: "cover",
									display: "block",
								}}
							/>
							<div
								style={{
									position: "absolute",
									top: 0,
									left: 0,
									width: "100%",
									height: "100%",
									pointerEvents: "none",
								}}
							>
								<div
									style={{
										position: "absolute",
										top: "50%",
										left: "50%",
										transform: "translate(-50%, -50%)",
										width: "80%",
										height: "33%",
										border: "2px solid white",
										borderRadius: "4px",
									}}
								/>
								<div
									style={{
										position: "absolute",
										bottom: "10px",
										left: "50%",
										transform: "translateX(-50%)",
										backgroundColor: "rgba(0,0,0,0.7)",
										color: "white",
										padding: "5px 10px",
										borderRadius: "4px",
										fontSize: "12px",
									}}
								>
									Position barcode within the box
								</div>
							</div>
						</div>
						<canvas ref={canvasRef} style={{ display: "none" }} />
						<div
							className="button-container mb-5"
							style={{
								display: "flex",
								gap: "10px",
								marginTop: "10px",
								alignItems: "center",
							}}
						>
							<button
								onClick={stopCamera}
								className="btn btn-outline-secondary"
								style={{ flex: 1 }}
							>
								Cancel
							</button>
							<button
								onClick={captureImage}
								className="btn btn-primary"
								style={{ flex: 1 }}
								disabled={isLoading || !isCameraActive}
							>
								{isLoading ? "Processing..." : "Capture"}
							</button>
							<label
								className="btn btn-outline-secondary mb-0"
								style={{
									flex: 1,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								Select
								<input
									type="file"
									accept="image/*"
									onChange={handleFileSelect}
									style={{ display: "none" }}
								/>
							</label>
						</div>{" "}
						{error && (
							<div
								className="error-message"
								style={{
									color: "#721c24",
									backgroundColor: "#f8d7da",
									padding: "10px",
									borderRadius: "4px",
									marginTop: "10px",
									fontSize: "14px",
									textAlign: "center",
								}}
							>
								{error}
								{error.includes("No barcode detected") && (
									<div style={{ marginTop: "8px" }}>
										<button
											onClick={startCamera}
											className="btn btn-sm btn-outline-danger"
										>
											Try Again
										</button>
									</div>
								)}
							</div>
						)}
					</div>
				</>
			) : (
				<div
					style={{
						backgroundColor: "green",
						color: "#155724",
						padding: "10px",
						borderRadius: "4px",
						marginBottom: "15px",
						textAlign: "center",
					}}
				>
					License scanned successfully!
					<div style={{ marginTop: "8px" }}>
						<button
							onClick={() => {
								resetScanner();
								startCamera();
							}}
							className="btn btn-sm btn-outline-success"
						>
							Scan Again
						</button>
						<button
							onClick={stopCamera}
							className="btn btn-sm btn-outline-secondary ml-2"
							style={{ marginLeft: "8px" }}
						>
							Done
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default LicenseScanner;
